import { useTranslation } from 'react-i18next';
import { useFriend } from '@/hooks/contacts/use-friends';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { generateJoinApplicationRemark } from '@/hooks/groups/use-group-join-applications';
import { useIsInternalUser } from '@/hooks/users/use-user-info';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { transformMessageTextByType } from '@/utils/transform-message-text-by-type';
import { translateComplexKey } from '@/utils/translate';
import type { ContentCommonProps } from './content-common-props';

type Props = Pick<ContentCommonProps, 'message'>;

export default function ContentAnnouncement({ message }: Props) {
  const isGroup = 'GroupID' in message;
  const { t } = useTranslation();
  const { isInternalUser } = useIsInternalUser();
  const { canManage } = useGroupInfo(isGroup ? message.GroupID : null);
  const { friend } = useFriend(message.UserID);

  const transformMessage = (message: ConversationMessageTypeUnion): string => {
    const friendName = friend?.RemarkName || friend?.FriendNickName || '';

    if (!message.Message.includes('|')) {
      return transformMessageTextByType(message, friendName, t);
    }

    if (message.MsgType === ConversationMessageContentType.JoinGroup) {
      const [originalMessage = '', newcomerName, source, remark] = message.Message.split('|');

      let content = t('message.join', { user: friendName || newcomerName });
      if (isInternalUser && canManage) {
        content += ` (${translateComplexKey(generateJoinApplicationRemark(source, remark, originalMessage), t)})`;
      }

      return content;
    }

    if (message.MsgType === ConversationMessageContentType.Notice) {
      const [content = ''] = message.Message.split('|');
      return content;
    }

    if (message.MsgType === ConversationMessageContentType.LeaveGroup) {
      const [originalMessage = '', leaveType, targetName = friendName, adminName = ''] = message.Message.split('|');
      const isKicked = leaveType ? leaveType === 'kick' : targetName && !originalMessage.includes(targetName);
      return isKicked
        ? t('message.kick', {
            user: friendName || targetName,
            admin: adminName || originalMessage,
          })
        : t('message.leave', { user: friendName || targetName });
    }

    return message.Message;
  };

  if (!canManage && message.MsgType === ConversationMessageContentType.LeaveGroup) return null;

  return (
    <div
      key={message.ID}
      className='my-3 w-full text-center text-body2 text-neutral-950'>
      {transformMessage(message)}
    </div>
  );
}
